import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { injectIntl, WrappedComponentProps } from '@kyruus/intl';
import { DialogContent } from '@biome-design-system/ui/DialogContent';
import {
  FilterWizard,
  useFilterWizard,
  FilterWizardProvider,
  FilterWizardEventType,
  type FilterWizardEventData,
  type FilterWizardRefiner
} from '@biome-design-system/toolkit/FilterWizard';
import { Dialog } from '@biome-design-system/ui/Dialog';

import { GetUpdatedSearch, SearchModification, type Log } from 'Src/types';
import { messages } from './messages';
import { getAnswerQueryModifications } from './utils';

const RESET_REFINER_QUERY_MODS: SearchModification[] = [
  { action: 'delete_key', key: 'patient_search_refiner' },
  { action: 'delete_key', key: 'psr_refined' },
  {
    action: 'append',
    key: 'patient_search_refiner',
    value: 'false'
  }
];
export interface BasePatientSearchRefinerProps {
  getUpdatedSearch: GetUpdatedSearch;
  log: Log;
  onClose: () => void;
  patientSearchRefiner: FilterWizardRefiner;
}

type PatientSearchRefinerProps = WrappedComponentProps &
  BasePatientSearchRefinerProps;

export const PatientSearchRefiner = injectIntl(
  ({
    getUpdatedSearch,
    intl,
    log,
    onClose,
    patientSearchRefiner
  }: PatientSearchRefinerProps) => {
    const history = useHistory();

    const handleClose = useCallback(
      (urlModifications: SearchModification[]) => {
        const newUrl = getUpdatedSearch(urlModifications);
        history.push(newUrl);
        onClose();
      },
      [getUpdatedSearch, history, onClose]
    );

    const handleEvent = useCallback(
      (eventType: FilterWizardEventType, data: FilterWizardEventData) => {
        const eventData = {
          initialSearch: patientSearchRefiner.question.text,
          ...data
        };
        const queryModifications = getAnswerQueryModifications(data.answer);

        switch (eventType) {
          case FilterWizardEventType.Enter:
            log('user_action.refined_search.opened', eventData);
            break;
          case FilterWizardEventType.Back:
            log('user_action.refined_search.back', eventData);
            break;
          case FilterWizardEventType.Exit:
            log('user_action.refined_search.skip', eventData);
            handleClose([
              ...RESET_REFINER_QUERY_MODS,
              { action: 'append', key: 'psr_refined', value: 'false' }
            ]);
            break;
          case FilterWizardEventType.Next:
            log('user_action.refined_search.next', eventData);
            break;
          case FilterWizardEventType.Complete:
            log('user_action.refined_search.completed', eventData);

            handleClose([
              ...RESET_REFINER_QUERY_MODS,
              { action: 'append', key: 'psr_refined', value: 'true' },
              ...queryModifications
            ]);
            break;
        }
      },
      [handleClose, log, patientSearchRefiner.question.text]
    );

    const wizardState = useFilterWizard({
      onEvent: handleEvent,
      questions: patientSearchRefiner
    });

    return (
      <Dialog
        aria-labelledby="filter-wizard-question-text"
        aria-modal="true"
        data-testid="patient-search-refiner-dialog"
        fullScreen={true}
        onClose={() => wizardState.handleExit()}
        open={true}
      >
        <DialogContent>
          <FilterWizardProvider wizardState={wizardState}>
            <FilterWizard
              data-testid="filter-wizard"
              disclaimer={intl.formatMessage(messages.disclaimer)}
              header={intl.formatMessage(messages.header)}
              hideSkip={false}
              labels={{
                back: intl.formatMessage(messages.backButtonLabel),
                noResults: intl.formatMessage(messages.noResultsLabel),
                progress: (progress: number) =>
                  intl.formatMessage(messages.progressCompleteLabel, {
                    percent: Math.round(progress)
                  }),
                skip: intl.formatMessage(messages.skipButtonLabel)
              }}
              showDisclaimerOnFirstStepOnly={true}
              showHeaderOnFirstStepOnly={true}
              showSkipOnFirstStepOnly={true}
            />
          </FilterWizardProvider>
        </DialogContent>
      </Dialog>
    );
  }
);
